import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  TimelineCalendarColumn,
  TimelineCalendarEvent,
  TimelineCalendarRow,
  ColumnGenerator,
} from '../utils/timelineUtils';
import {
  findEndColumn,
  findStartColumn,
  generateGraphColors,
  generateRowGraphs,
} from '../utils/timelineUtils';
import { InfiniteScrollObserver } from './InfiniteScrollObserver';
import { InteractJSResizeEvent } from '../core/plugins/interactjs';

export type TimelineCalendarOptions = {
  columnWindowSize?: number; // Number of columns to display at once
  eventHeight?: number; // Height of each event block
  columnWidth?: number; // Width of each column
  columnHeight?: number; // Height of the column header
  rowWidth?: number; // Width of the row header
  columnWindowLocation?: number; // Starting index for the column window
  backgroundColor?: string; // Background color for the calendar
};

export interface TimelineCalendarProps<E = unknown, R = unknown, C = unknown> {
  rows: TimelineCalendarRow<R>[];
  events: TimelineCalendarEvent<E>[];
  className?: string;
  columnGenerator: ColumnGenerator<C>;
  renderRowHeader?: (
    row: TimelineCalendarRow<R>,
    rowMetadata: {
      rowEvents: TimelineCalendarEvent<E>[];
      segments: Array<{ title: string; colors: Set<number> }>;
    },
  ) => React.ReactNode;
  renderColumnHeader?: (column: TimelineCalendarColumn<C>) => React.ReactNode;
  renderEvent?: (
    event: TimelineCalendarEvent<E>,
    style: React.CSSProperties,
  ) => React.ReactNode;
  sortEvents?: (
    eventA: TimelineCalendarEvent<E>,
    eventB: TimelineCalendarEvent<E>,
  ) => number;
  onScrollX?: () => void;
  onScrollY?: () => void;
  onResize?: (options: TimelineCalendarOptions) => void;
  options?: TimelineCalendarOptions;
}

export type TimelineCalendarRef = {
  scrollToColumn: (columnIndex: number) => void;
  scrollToRow: (rowIndex: number) => void;
  setScrollX: (scrollX: number) => void;
  setScrollY: (scrollY: number) => void;
  getContentSize: () => { width: number; height: number };
  getScrollPosition: () => number;
};

const COLUMN_WINDOW_SIZE = 14; // Default number of columns to display at once
const EVENT_HEIGHT = 50; // Default fixed height for each row
const COLUMN_HEIGHT = 50; // Default fixed height for each column
const COLUMN_WIDTH = 128; // Default fixed width for each column
const ROW_WIDTH = 100; // Default fixed width for each row
const COLUMN_WINDOW_LOCATION = 0; // Default starting index for the column window

const _TimelineCalendar = <
  E extends unknown,
  R extends unknown,
  C extends unknown,
>(
  {
    rows,
    events,
    className,
    columnGenerator,
    renderColumnHeader,
    renderRowHeader,
    renderEvent,
    sortEvents,
    onScrollX,
    onScrollY,
    onResize,
    options = {},
  }: TimelineCalendarProps<E, R, C>,
  ref: React.Ref<TimelineCalendarRef>,
) => {
  const {
    columnWindowSize = COLUMN_WINDOW_SIZE,
    eventHeight = EVENT_HEIGHT,
    columnWidth = COLUMN_WIDTH,
    columnHeight = COLUMN_HEIGHT,
    rowWidth: _rowWidth = ROW_WIDTH,
    columnWindowLocation = COLUMN_WINDOW_LOCATION,
  } = options;

  const [rowWidth, setRowWidth] = useState(_rowWidth);
  const [columns, setColumns] = useState<TimelineCalendarColumn<C>[]>([]);
  const [scrollLeft, setScrollLeft] = useState(0);

  const contentContainerRef = useRef<HTMLDivElement>(null);
  const rowHeaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = (event: Event) => {
      const resizeEvent = event as InteractJSResizeEvent;
      setRowWidth(resizeEvent.dimensions.x);
      onResize?.({ ...options, rowWidth: resizeEvent.dimensions.x });
    };

    if (rowHeaderRef.current) {
      rowHeaderRef.current.addEventListener(
        'interactjs-resize-end',
        handleResize,
      );
      return () => {
        rowHeaderRef.current?.removeEventListener(
          'interactjs-resize-end',
          handleResize,
        );
      };
    }

    return () => {};
  }, [rowHeaderRef.current]);

  useEffect(() => {
    setRowWidth(_rowWidth);
  }, [_rowWidth]);

  useEffect(() => {
    const newColumns: TimelineCalendarColumn<C>[] = [];
    for (
      let i = columnWindowLocation;
      i < columnWindowLocation + columnWindowSize;
      i++
    ) {
      newColumns.push(columnGenerator(i));
    }
    setColumns(newColumns);
  }, [columnWindowLocation, columnWindowSize, columnGenerator]);

  useEffect(() => {
    const handleScroll = (evt: Event) => {
      const target = evt.target as HTMLDivElement;
      setScrollLeft(target.scrollLeft);
    };
    contentContainerRef.current?.addEventListener('scroll', handleScroll);
    return () => {
      contentContainerRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [contentContainerRef?.current]);

  const sortedEvents = useMemo(() => {
    const eventsCopy = events.slice();
    if (sortEvents) {
      eventsCopy.sort(sortEvents);
    }
    return eventsCopy;
  }, [events, sortEvents]);

  const colors = useMemo(() => {
    const graphs = generateRowGraphs(sortedEvents, columns);
    const rowColors: Record<string, [number, Record<string, number>]> = {};
    Object.entries(graphs).forEach(([rowId, graph]) => {
      rowColors[rowId] = generateGraphColors(graph);
    });
    return rowColors;
  }, [sortedEvents, columns]);

  const rowOffset = useMemo(() => {
    const offset: Record<string, number> = {};
    let currentOffset = 0;
    rows.forEach((row) => {
      const rowColorCount = colors[row.id]?.[0] ?? 1;
      offset[row.id] = currentOffset;
      currentOffset += rowColorCount * eventHeight;
    });
    return offset;
  }, [rows, colors]);

  const scrollToColumn = useCallback(
    (column: number) => {
      if (!contentContainerRef.current || column < 0) return;
      const columnX = column * columnWidth;
      contentContainerRef.current.scrollLeft = columnX;
      setScrollLeft(columnX);
    },
    [columnWidth, contentContainerRef],
  );

  // Calculate total width of the columns container
  const contentWidth = columnWindowSize * columnWidth;
  const totalWidth = contentWidth + rowWidth;
  const contentHeight = useMemo(
    () =>
      rows.reduce((acc, row) => {
        const rowColorCount = colors[row.id]?.[0] ?? 1;
        return acc + rowColorCount * eventHeight;
      }, 0),
    [rows, colors, eventHeight],
  );

  useImperativeHandle(
    ref,
    () => ({
      scrollToColumn,
      scrollToRow: (_rowIndex: number) => {
        // Not implemented since rows are not scrollable in this design
      },
      setScrollX: (scrollX: number) => {
        if (contentContainerRef.current) {
          contentContainerRef.current.scrollLeft = scrollX;
          setScrollLeft(scrollX);
        }
      },
      setScrollY: (_scrollY: number) => {
        // Not implemented since rows are not scrollable in this design
      },
      getContentSize: () => {
        if (contentContainerRef.current) {
          return {
            width: contentWidth,
            height: contentContainerRef.current.scrollHeight,
          };
        }
        return { width: 0, height: 0 };
      },
      getScrollPosition: () => {
        return scrollLeft;
      },
    }),
    [contentWidth, contentContainerRef.current?.scrollHeight, scrollLeft],
  );

  // Set the width of the columns container
  useEffect(() => {
    if (contentContainerRef.current) {
      contentContainerRef.current.style.width = `${totalWidth}px`;
    }
  }, [totalWidth]);

  return (
    <div ref={contentContainerRef}>
      <div
        className="timeline-calendar-headers"
        style={{
          backgroundColor: options.backgroundColor,
          height: `${columnHeight}px`,
        }}
      >
        <div
          className={
            scrollLeft > 0
              ? 'timeline-calendar-corner'
              : 'timeline-calendar-corner text-muted'
          }
          style={{
            width: `${rowWidth}px`,
            cursor: scrollLeft > 0 ? 'pointer' : 'not-allowed',
            backgroundColor: options.backgroundColor,
          }}
        ></div>
        <div className="d-flex h-100">
          {Object.values(columns).map((column) => (
            <div
              key={column.id}
              className="timeline-calendar-column-header"
              style={{
                width: `${columnWidth}px`,
                backgroundColor: options.backgroundColor,
              }}
            >
              {renderColumnHeader ? renderColumnHeader(column) : column.title}
            </div>
          ))}
        </div>
      </div>
      <div className={`timeline-calendar-board ${className ?? ''}`}>
        <div
          className="timeline-calendar-rows stripped"
          style={{ backgroundColor: options.backgroundColor }}
          ref={rowHeaderRef}
        >
          {rows.map((row) => {
            const rowColors = colors[row.id]?.[1] ?? {};
            const rowColorCount = colors[row.id]?.[0] ?? 1;
            const height = eventHeight * rowColorCount;
            const rowEvents = sortedEvents.filter(
              (event) => event.row === row.id,
            );
            const segments = rowEvents.reduce<
              Array<{ title: string; colors: Set<number> }>
            >((acc, event) => {
              if (
                acc.length === 0 ||
                acc[acc.length - 1].title !== event.segment
              ) {
                acc.push({
                  title: event.segment ?? '',
                  colors: new Set<number>([rowColors[event.id]]),
                });
              } else {
                acc[acc.length - 1].colors.add(rowColors[event.id]);
              }
              return acc;
            }, []);

            return (
              <div
                key={row.id}
                style={{
                  width: `${rowWidth}px`,
                  height: `${height}px`,
                }}
                className="timeline-calendar-row-header resizable-end"
              >
                {renderRowHeader
                  ? renderRowHeader(row, {
                      rowEvents,
                      segments,
                    })
                  : row.title}
                <div className="draggable-border draggable-border-end"></div>
              </div>
            );
          })}
        </div>
        <div
          className="timeline-calendar-columns"
          style={{
            width: `${contentWidth}px`,
          }}
        >
          {Object.values(columns).map((column) => (
            <div
              key={column.id}
              className="timeline-calendar-column"
              style={{
                width: `${columnWidth}px`,
              }}
            >
              {rows.map((row) => {
                const rowColorCount = colors[row.id]?.[0] ?? 1;
                const height = eventHeight * rowColorCount;

                return (
                  <div
                    key={`${row.id}-${column.id}`}
                    className="timeline-calendar-cell"
                    style={{
                      height: `${height}px`,
                      width: `${columnWidth}px`,
                    }}
                  ></div>
                );
              })}
            </div>
          ))}
        </div>
        <div
          className="timeline-calendar-events"
          style={{
            maxWidth: `${contentWidth}px`,
          }}
        >
          {sortedEvents.map((event) => {
            const startColumn = findStartColumn(columns, event.start);
            const endColumn = findEndColumn(columns, event.end);
            const row = rows.find((r) => r.id === event.row);

            if (startColumn && endColumn && row) {
              const rowColors = colors[row.id]?.[1] ?? {};
              const color = rowColors[event.id] ?? 0;
              const startColumnIndex = 1 + startColumn[0];
              const endColumnIndex = 1 + endColumn[0];
              const eventStartX =
                rowWidth +
                (startColumnIndex - columnWindowLocation) * columnWidth -
                scrollLeft;
              const eventEndX =
                rowWidth +
                (endColumnIndex - columnWindowLocation) * columnWidth -
                scrollLeft;
              const eventY = rowOffset[row.id];
              const cappedEventEndX = Math.min(
                eventEndX,
                rowWidth + contentWidth,
              );
              const eventWidth = cappedEventEndX - eventStartX;

              return (
                <div
                  key={event.id}
                  style={{
                    left: `${eventStartX}px`,
                    top: `${eventY + color * eventHeight}px`,
                    width: `${eventWidth}px`,
                    height: `${eventHeight}px`,
                  }}
                  className="timeline-calendar-event-container"
                >
                  {renderEvent ? (
                    renderEvent(event, {
                      width: '100%',
                      height: '100%',
                      boxSizing: 'border-box',
                    })
                  ) : (
                    <div className="timeline-calendar-default-event">
                      {event.title}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })}
        </div>
        <InfiniteScrollObserver
          style={{
            height: `${contentHeight}px`,
          }}
          onScrollIn={onScrollX}
        />
      </div>
      <InfiniteScrollObserver
        style={{ width: '100%' }}
        onScrollIn={onScrollY}
      />
    </div>
  );
};

export const TimelineCalendar = forwardRef<
  TimelineCalendarRef,
  TimelineCalendarProps<any, any, any>
>(_TimelineCalendar);
